import React, { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Icon from "../components/ui/Icon";
import IconBadge from "../components/ui/IconBadge";
import EstadoVazio from "../components/ui/EstadoVazio";

// Conteúdo estático — cada pergunta diz pra quem ela vale (funcionário e/ou
// gestor também usam bater ponto, então a maioria aparece pros dois; só o
// que é tela exclusiva de gestor fica marcado só como "gestor").
const CATEGORIAS = [
  {
    nome: "Bater ponto",
    icon: "clock",
    perguntas: [
      {
        perfis: ["funcionario", "gestor"],
        q: "Como eu bato o ponto?",
        r: "Na tela Bater Ponto, clique no círculo grande (\"Bater Entrada\" ou \"Bater Saída\", dependendo do que falta). O sistema pede uma selfie na hora, pela câmera, e sua localização, se o posto exigir. Depois de confirmar a foto, a batida já fica registrada.",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "Por que preciso tirar uma selfie pra bater o ponto?",
        r: "É a forma do sistema confirmar que foi você mesmo quem bateu, no horário certo. A foto (e a localização, quando aplicável) só fica visível pro seu gestor, nunca é pública.",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "O sistema recusou minha batida por causa da localização, por quê?",
        r: "Alguns postos exigem que você esteja fisicamente dentro de um raio (em metros) da coordenada cadastrada, ou conectado pelo IP fixo do escritório. Se aparecer esse aviso e você estiver no lugar certo, o mais comum é o GPS do computador ser impreciso — tente pelo celular com o GPS ligado, ou avise seu gestor pra conferir o cadastro do posto.",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "O que é \"folga pela escala\" e por que preciso justificar?",
        r: "Quando sua escala configurada diz que hoje não é seu dia de trabalho mas você bate o ponto mesmo assim (por exemplo, cobrindo um colega), o sistema pede uma justificativa e a batida fica pendente até seu gestor aprovar — é só nesse caso, em um dia normal de trabalho não pede nada disso.",
      },
    ],
  },
  {
    nome: "Férias, atestado e feriado",
    icon: "heart",
    perguntas: [
      {
        perfis: ["funcionario", "gestor"],
        q: "Como eu lanço um atestado médico?",
        r: "Na tela \"Meu Histórico\", clique em \"Lançar Atestado\". Informe o período, anexe uma foto ou PDF do documento, confirme a autorização de envio (é dado sensível de saúde, a LGPD exige esse aceite específico) e envie. Fica pendente até seu gestor decidir.",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "Quem vê o atestado que eu enviei?",
        r: "Só o seu gestor, e só pra decidir se aprova ou recusa a ausência. Se for recusado, o arquivo é apagado do sistema automaticamente — a empresa não fica guardando documento de saúde que não aceitou.",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "Como sei se meu atestado foi aprovado ou recusado?",
        r: "No card \"Meus afastamentos\", logo no topo da tela \"Meu Histórico\" — mostra o status (aguardando, aprovado ou recusado) de cada um que você já enviou. Nos dias cobertos por um atestado aprovado, o status na tabela também muda pra \"Atestado médico\" em vez de \"Falta\".",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "O que acontece no meu ponto quando é feriado?",
        r: "O sistema já sabe quando é feriado (o gestor cadastra) — você não é cobrado de bater ponto nesse dia. Se trabalhar mesmo assim, é só bater igual sempre, sem precisar justificar nada.",
      },
      {
        perfis: ["gestor"],
        q: "Como lanço férias, atestado ou outro afastamento pra um funcionário?",
        r: "Em \"Atrasos e Faltas\", clique no incidente do funcionário e escolha \"Justificar\" — selecione o período e o tipo (férias, atestado ou outro), anexando o documento se tiver. Já entra aprovado na hora, já que é você concedendo.",
      },
    ],
  },
  {
    nome: "Banco de horas e hora extra",
    icon: "barChart",
    perguntas: [
      {
        perfis: ["funcionario", "gestor"],
        q: "Como funciona o banco de horas?",
        r: "Se faltar horas em um dia pra completar sua jornada, você pode declarar que está usando o banco de horas pra cobrir a diferença — aparece na tela Bater Ponto assim que você bater a saída, se tiver saldo suficiente.",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "Onde vejo meu saldo de banco de horas?",
        r: "No card \"Saldo banco de horas\", na tela Bater Ponto — e com o detalhe dia a dia (o quanto cada dia somou ou tirou do saldo) na coluna \"Saldo acumulado\" de \"Meu Histórico\".",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "Trabalhei além do horário, isso vira hora extra automaticamente?",
        r: "O sistema detecta e cria um pedido de hora extra pendente, mas só entra no seu saldo depois que o gestor aprovar — não é automático nem imediato, é por isso que às vezes aparece um aviso de \"aguardando aprovação\" na tela Bater Ponto.",
      },
    ],
  },
  {
    nome: "Aprovações",
    icon: "checkCircle",
    perguntas: [
      {
        perfis: ["gestor"],
        q: "O que eu preciso aprovar como gestor?",
        r: "Só uma coisa fica pendente até você decidir: horas extras detectadas automaticamente pelo sistema, em \"Aprovações Pendentes\". Atestado e outros afastamentos você mesmo lança já aprovado, em \"Atrasos e Faltas\".",
      },
    ],
  },
  {
    nome: "Cadastro de funcionários e postos",
    icon: "users",
    perguntas: [
      {
        perfis: ["gestor"],
        q: "Como cadastro um funcionário?",
        r: "Em \"Funcionários\", clique em cadastrar um novo — preencha os dados e a escala de trabalho (horário, tipo de escala). Pra dar acesso ao sistema, vincule ao login de acesso já criado pra essa pessoa.",
      },
      {
        perfis: ["gestor"],
        q: "Dá pra cadastrar vários funcionários de uma vez?",
        r: "Sim, em \"Funcionários\" tem \"Baixar modelo CSV\" (uma planilha de exemplo com as colunas certas) e \"Importar CSV\" (sobe a planilha preenchida). Só o nome é obrigatório — posto e empresa são encontrados pelo nome, e uma linha com erro não derruba as outras.",
      },
      {
        perfis: ["gestor"],
        q: "Como configuro um posto de trabalho e a localização exigida?",
        r: "Em \"Postos\", cadastre o nome e a localização — clicando no mapa, digitando ou colando as coordenadas do Google Maps, ou usando \"usar minha localização atual\" estando fisicamente lá — e o raio permitido em metros. Dá pra desligar a exigência de localização (fica só informativo) ou usar o IP fixo do escritório como alternativa, pra quem bate ponto de computador sem GPS confiável.",
      },
      {
        perfis: ["gestor"],
        q: "Como cadastro os feriados?",
        r: "Em \"Feriados\" tem um botão pra importar automaticamente os feriados nacionais do ano (fixos e móveis, como Carnaval e Sexta-feira Santa), ou cadastrar um feriado específico manualmente.",
      },
    ],
  },
  {
    nome: "Relatórios e histórico",
    icon: "fileText",
    perguntas: [
      {
        perfis: ["gestor"],
        q: "Como vejo o histórico de ponto de um funcionário?",
        r: "Em \"Funcionários\" > \"Folha de Ponto\", selecione o funcionário — mostra dia a dia do mês, com horários, horas trabalhadas, saldo e status de cada dia.",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "Como exporto meus dados pra Excel ou PDF?",
        r: "Nas telas de histórico tem os botões \"Baixar CSV\" (abre certinho no Excel) e \"Imprimir / PDF\" (usa a função de imprimir do próprio navegador — escolha \"Salvar como PDF\" no lugar de uma impressora).",
      },
      {
        perfis: ["gestor"],
        q: "O que é a tela de Auditoria?",
        r: "Registra toda vez que uma batida de ponto é excluída — quem excluiu, quando e o motivo. Excluir uma batida é uma ação sensível (apaga uma evidência), então fica sempre um rastro de quem fez e por quê.",
      },
    ],
  },
  {
    nome: "Conta e segurança",
    icon: "user",
    perguntas: [
      {
        perfis: ["funcionario", "gestor"],
        q: "Como troco minha senha?",
        r: "Em \"Meus Dados\", na seção \"Acesso\", informe a senha atual e a nova.",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "Como ativo a autenticação em duas etapas (2FA)?",
        r: "Em \"Meus Dados\", seção \"Acesso\", clique em \"Ativar\". Abra o link em um app autenticador (Google Authenticator, Authy) ou digite o código secreto manualmente, e confirme com o código de 6 dígitos que aparecer no app.",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "Perdi meu celular e não consigo mais entrar, e agora?",
        r: "Peça pra alguém com perfil de gestor na sua empresa desativar seu 2FA na tela \"Usuários\" — só o gestor consegue fazer isso por outra pessoa, nunca a própria pessoa sozinha (senão o 2FA não protegeria contra quem já roubou a senha). Se você mesmo é o único gestor da empresa, essa tela não ajuda, já que nem dá pra entrar pra usá-la — nesse caso é preciso suporte técnico direto.",
      },
      {
        perfis: ["funcionario", "gestor"],
        q: "Como atualizo meu telefone ou e-mail de contato?",
        r: "Em \"Meus Dados\", seção \"Contato\" — esse é o único bloco de dados que você mesmo mantém. Os outros (nome, cargo, CPF, escala etc.) são controlados pelo seu gestor.",
      },
    ],
  },
];

export default function Ajuda() {
  const { usuario } = useAuth();
  const [busca, setBusca] = useState("");
  const [abertas, setAbertas] = useState(() => new Set());

  const termo = busca.trim().toLowerCase();

  const categoriasVisiveis = useMemo(() => {
    return CATEGORIAS.map((cat) => {
      const perguntas = cat.perguntas.filter((p) => {
        if (!p.perfis.includes(usuario?.perfil)) return false;
        if (!termo) return true;
        return p.q.toLowerCase().includes(termo) || p.r.toLowerCase().includes(termo);
      });
      return { ...cat, perguntas };
    }).filter((cat) => cat.perguntas.length > 0);
  }, [usuario?.perfil, termo]);

  const alternar = (chave) => {
    setAbertas((atual) => {
      const novo = new Set(atual);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      return novo;
    });
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
          Ajuda
        </h1>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
          Dúvidas comuns sobre como usar o sistema, por tema.
        </p>
      </div>

      <div style={{ position: "relative", marginBottom: 24, maxWidth: 420 }}>
        <div style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: COLORS.textLight, display: "flex" }}>
          <Icon name="search" size={16} />
        </div>
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por uma palavra, ex: atestado, senha, localização..."
          style={{
            width: "100%", boxSizing: "border-box", border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md,
            padding: "11px 14px 11px 40px", fontSize: 14, color: COLORS.text, background: COLORS.surface,
          }}
        />
      </div>

      {categoriasVisiveis.length === 0 ? (
        <Card style={{ padding: 0 }}>
          <EstadoVazio icon="helpCircle" texto="Nenhum resultado pra essa busca — tenta outra palavra." />
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {categoriasVisiveis.map((cat) => (
            <Card key={cat.nome} style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}` }}>
                <IconBadge icon={cat.icon} size={32} cor={COLORS.primary} />
                <h2 style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: 0 }}>{cat.nome}</h2>
              </div>
              <div>
                {cat.perguntas.map((p) => {
                  const chave = `${cat.nome}-${p.q}`;
                  const aberta = !!termo || abertas.has(chave);
                  return (
                    <div key={chave} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <button
                        onClick={() => alternar(chave)}
                        style={{
                          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                          padding: "14px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left",
                        }}
                      >
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{p.q}</span>
                        <span style={{ flexShrink: 0, color: COLORS.textLight, display: "flex", transform: aberta ? "rotate(180deg)" : "none", transition: "transform .15s" }}>
                          <Icon name="chevronDown" size={16} />
                        </span>
                      </button>
                      {aberta && (
                        <p style={{ fontSize: 13, color: COLORS.textMid, lineHeight: 1.65, margin: "0 20px 16px" }}>
                          {p.r}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
